import apiClient, { getErrorMessage } from './api.service';
import { API_ENDPOINTS } from '../config/api';

export interface Announcement {
  _id: string;
  title: string;
  content: string;
  author: {
    _id: string;
    name: string;
    email: string;
  };
  recipients: Array<{
    _id: string;
    name: string;
    email: string;
  }>;
  isPublic: boolean;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  isActive: boolean;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  isRead?: boolean;
  readAt?: string | null;
  readCount?: number;
  totalRecipients?: number;
  readPercentage?: number;
}

export interface CreateAnnouncementData {
  title: string;
  content: string;
  recipients?: string[];
  isPublic?: boolean;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  expiresAt?: string;
}

export interface UpdateAnnouncementData {
  title?: string;
  content?: string;
  recipients?: string[];
  isPublic?: boolean;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  expiresAt?: string;
  isActive?: boolean;
}

export interface AnnouncementResponse {
  status: string;
  message: string;
  data: {
    announcement: Announcement;
  };
}

export interface AnnouncementsResponse {
  status: string;
  data: {
    announcements: Announcement[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
    unreadCount?: number;
  };
}

export interface Student {
  _id: string;
  name: string;
  email: string;
  studentId?: string;
}

export interface StudentsResponse {
  status: string;
  data: {
    students: Student[];
  };
}

class AnnouncementService {
  async getAnnouncements(params?: {
    page?: number;
    limit?: number;
    unreadOnly?: boolean;
  }): Promise<AnnouncementsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.unreadOnly) queryParams.append('unreadOnly', 'true');

      const url = params && Object.keys(params).length > 0 
        ? `${API_ENDPOINTS.ANNOUNCEMENTS.GET_ALL}?${queryParams.toString()}`
        : API_ENDPOINTS.ANNOUNCEMENTS.GET_ALL;

      const response = await apiClient.get<AnnouncementsResponse>(url);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async getAnnouncementsForAdmin(params?: {
    page?: number;
    limit?: number;
    status?: 'all' | 'active' | 'inactive';
  }): Promise<AnnouncementsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.status) queryParams.append('status', params.status);

      const url = params && Object.keys(params).length > 0 
        ? `${API_ENDPOINTS.ANNOUNCEMENTS.GET_ADMIN}?${queryParams.toString()}`
        : API_ENDPOINTS.ANNOUNCEMENTS.GET_ADMIN;

      const response = await apiClient.get<AnnouncementsResponse>(url);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async createAnnouncement(data: CreateAnnouncementData): Promise<AnnouncementResponse> {
    try {
      const response = await apiClient.post<AnnouncementResponse>(
        API_ENDPOINTS.ANNOUNCEMENTS.CREATE,
        data
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async updateAnnouncement(id: string, data: UpdateAnnouncementData): Promise<AnnouncementResponse> {
    try {
      const response = await apiClient.put<AnnouncementResponse>(
        API_ENDPOINTS.ANNOUNCEMENTS.UPDATE(id),
        data
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async deleteAnnouncement(id: string): Promise<{ status: string; message: string }> {
    try {
      const response = await apiClient.delete<{ status: string; message: string }>(
        API_ENDPOINTS.ANNOUNCEMENTS.DELETE(id)
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async markAsRead(id: string): Promise<{ status: string; message: string }> {
    try {
      const response = await apiClient.post<{ status: string; message: string }>(
        API_ENDPOINTS.ANNOUNCEMENTS.MARK_READ(id)
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async markAsUnread(id: string): Promise<{ status: string; message: string }> {
    try {
      const response = await apiClient.delete<{ status: string; message: string }>(
        API_ENDPOINTS.ANNOUNCEMENTS.MARK_UNREAD(id)
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async clearAllAnnouncements(): Promise<{ status: string; message: string }> {
    try {
      const response = await apiClient.post<{ status: string; message: string }>(
        API_ENDPOINTS.ANNOUNCEMENTS.CLEAR_ALL
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async clearReadAnnouncements(): Promise<{ status: string; message: string }> {
    try {
      const response = await apiClient.delete<{ status: string; message: string }>(
        API_ENDPOINTS.ANNOUNCEMENTS.CLEAR_READ
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async getStudents(): Promise<StudentsResponse> {
    try {
      const response = await apiClient.get<StudentsResponse>(
        API_ENDPOINTS.ANNOUNCEMENTS.GET_STUDENTS
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }
}

const announcementService = new AnnouncementService();
export default announcementService;
